import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, Sun, Moon, User as UserIcon, Home, Bike, Users, Plus } from 'lucide-react';
import MyRides from '../components/MyRides';
import BrowseRides from '../components/BrowseRides';
import JoinedRides from '../components/JoinedRides';
import ClubsList from '../components/ClubsList';
import CreateRideModal from '../components/CreateRideModal';
import ProfilePictureModal from '../components/ProfilePictureModal';
import NotificationBell from '../components/NotificationBell';
import Profile from './Profile';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '../components/ThemeProvider';

export default function Dashboard({ user, onLogout, onUserUpdate }) {
  const [activeTab, setActiveTab] = useState('home');
  const [ridesSubTab, setRidesSubTab] = useState('my-rides');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const { theme, setTheme } = useTheme();

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/rydrlink-logo.jpg" 
              alt="RydrLink Logo" 
              className="h-10 w-auto"
            />
            <button
              onClick={() => setShowProfileModal(true)}
              className="relative group"
              data-testid="profile-picture-button"
            >
              {currentUser.profile_picture ? (
                <img
                  src={currentUser.profile_picture}
                  alt={currentUser.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-purple-600"
                />
              ) : (
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">
                    {currentUser.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full transition-all flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                Rydr<span className="text-purple-500">Link</span>
              </h1>
              <p className="text-sm text-muted-foreground">Welcome, {currentUser.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="flex items-center gap-2">
                <span className="text-foreground font-semibold">{currentUser.avg_rating.toFixed(1)}</span>
                <span className="text-yellow-500">⭐</span>
              </div>
              <p className="text-xs text-muted-foreground">{currentUser.total_ratings} ratings</p>
            </div>
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" onClick={onLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className={`dashboard-bg ${activeTab === 'home' ? 'browse-bg' : activeTab === 'rides' ? 'my-rides-bg' : 'joined-bg'}`}>
        <div className="container mx-auto px-4 py-6 dashboard-content">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between mb-6">
            <TabsList>
              <TabsTrigger value="home" data-testid="home-tab">
                <Home className="w-4 h-4 mr-2 hidden sm:inline" />Home
              </TabsTrigger>
              <TabsTrigger value="rides" data-testid="rides-tab">
                <Bike className="w-4 h-4 mr-2 hidden sm:inline" />Rides
              </TabsTrigger>
              <TabsTrigger value="clubs" data-testid="clubs-tab">
                <Users className="w-4 h-4 mr-2 hidden sm:inline" />Clubs
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="profile-tab">
                <UserIcon className="w-4 h-4 mr-2 hidden sm:inline" />Profile
              </TabsTrigger>
            </TabsList>
            <Button onClick={() => setShowCreateModal(true)} data-testid="create-ride-button">
              <Plus className="w-4 h-4 mr-2" />
              Create
            </Button>
          </div>

          {/* Home Tab - Browse Rides */}
          <TabsContent value="home" data-testid="home-content">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground mb-1">Find Your Next Ride</h2>
              <p className="text-muted-foreground">Swipe right to join, left to skip</p>
            </div>
            <BrowseRides user={currentUser} />
          </TabsContent>

          {/* Rides Tab - My Rides & Joined only */}
          <TabsContent value="rides" data-testid="rides-content">
            <Tabs value={ridesSubTab} onValueChange={setRidesSubTab} className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="my-rides">My Rides</TabsTrigger>
                <TabsTrigger value="joined">Joined</TabsTrigger>
              </TabsList>
              
              <TabsContent value="my-rides">
                <MyRides />
              </TabsContent>
              <TabsContent value="joined">
                <JoinedRides />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Clubs Tab */}
          <TabsContent value="clubs" data-testid="clubs-content">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground mb-1">Cycling Clubs</h2>
              <p className="text-muted-foreground">Join clubs to access exclusive rides and connect with local cyclists</p>
            </div>
            <ClubsList user={currentUser} />
          </TabsContent>

          <TabsContent value="profile" data-testid="profile-content">
            <Profile user={currentUser} onUpdate={refreshUser} />
          </TabsContent>
        </Tabs>
        </div>
      </div>

      {/* Create Ride Modal */}
      {showCreateModal && (
        <CreateRideModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            setActiveTab('my-rides');
          }}
        />
      )}

      {/* Profile Picture Modal */}
      {showProfileModal && (
        <ProfilePictureModal
          user={currentUser}
          onClose={() => setShowProfileModal(false)}
          onUpdated={refreshUser}
        />
      )}
    </div>
  );
}
